/**
 * Der kleinste Satz Vektor- und Drehmatrizenrechnung, den der Kern braucht.
 *
 * Bewusst eigen und nicht `three` — der Kern soll ohne Grafikbibliothek laufen.
 * Das sind vierzig Zeilen Mathematik, die sich nicht ändern; eine Abhängigkeit
 * von einer Rendering-Bibliothek dafür einzugehen, hiesse die Rechnung an den
 * Bildschirm zu binden, den sie gerade loswerden soll.
 *
 * ACHSEN: x nach vorn, y nach oben, z zur rechten Körperseite.
 *
 * Das ist NICHT das Koordinatensystem der Gelenkpunkte (dort: x vorn, y zur
 * linken Seite, z nach oben). Die Umrechnung passiert an einer Stelle, in
 * `kinematics.ts`, und ist dort auch beschriftet. Zwei Systeme sind einmal
 * lästig; zwei Systeme, die man an fünf Stellen ineinander umrechnet, sind eine
 * Fehlerquelle.
 */

export interface Vec3 {
  x: number
  y: number
  z: number
}

export const vec = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z })

export const add = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.x + b.x,
  y: a.y + b.y,
  z: a.z + b.z,
})

export const sub = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.x - b.x,
  y: a.y - b.y,
  z: a.z - b.z,
})

export const scale = (a: Vec3, k: number): Vec3 => ({
  x: a.x * k,
  y: a.y * k,
  z: a.z * k,
})

export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z

export const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
})

export const length = (a: Vec3): number => Math.sqrt(dot(a, a))

export const distance = (a: Vec3, b: Vec3): number => length(sub(a, b))

/** Lineare Mischung, t = 0 ergibt a, t = 1 ergibt b. */
export const lerpVec = (a: Vec3, b: Vec3, t: number): Vec3 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
})

/**
 * Drehmatrix, spaltenweise: `m[0..2]` ist die x-Achse des gedrehten Systems,
 * `m[3..5]` die y-Achse, `m[6..8]` die z-Achse.
 *
 * Spaltenweise und nicht zeilenweise, weil dann `apply` genau die Summe der
 * mit den Komponenten gewichteten Achsen ist — das ist die Form, in der man
 * die Matrix beim Lesen wiedererkennt.
 */
export type Mat3 = Float64Array

export const identity = (): Mat3 => Float64Array.from([1, 0, 0, 0, 1, 0, 0, 0, 1])

/** Dreht einen Vektor mit der Matrix. */
export function apply(m: Mat3, v: Vec3): Vec3 {
  return {
    x: m[0] * v.x + m[3] * v.y + m[6] * v.z,
    y: m[1] * v.x + m[4] * v.y + m[7] * v.z,
    z: m[2] * v.x + m[5] * v.y + m[8] * v.z,
  }
}

/** Verkettet zwei Drehungen: erst `b`, dann `a`. */
export function multiply(a: Mat3, b: Mat3): Mat3 {
  const out = new Float64Array(9)
  for (let c = 0; c < 3; c++) {
    for (let r = 0; r < 3; r++) {
      out[c * 3 + r] = a[r] * b[c * 3] + a[3 + r] * b[c * 3 + 1] + a[6 + r] * b[c * 3 + 2]
    }
  }
  return out
}

/** Transponierte — bei einer Drehmatrix zugleich die Umkehrung. */
export function transpose(m: Mat3): Mat3 {
  return Float64Array.from([m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]])
}

/**
 * Euler-Drehung in der Reihenfolge ZXY — erst um y, dann um x, dann um z.
 *
 * Diese Reihenfolge ist aus der Vorlage übernommen und nicht beliebig: bei
 * einem Beingelenk soll die Beugung (um z) die äusserste Drehung sein, damit
 * Ab- und Anspreizung (um x) und Drehung um die Längsachse (um y) im
 * Oberschenkelsystem bleiben. Eine andere Reihenfolge liefert bei gleichen
 * Winkeln eine andere Stellung — sichtbar erst, wenn zwei Achsen zugleich
 * ausgelenkt sind.
 *
 * Winkel in RADIANT.
 */
export function eulerZXY(x: number, y: number, z: number): Mat3 {
  const cx = Math.cos(x)
  const sx = Math.sin(x)
  const cy = Math.cos(y)
  const sy = Math.sin(y)
  const cz = Math.cos(z)
  const sz = Math.sin(z)

  // R = Rz · Rx · Ry
  const m = new Float64Array(9)
  m[0] = cz * cy - sz * sx * sy
  m[1] = sz * cy + cz * sx * sy
  m[2] = -cx * sy

  m[3] = -sz * cx
  m[4] = cz * cx
  m[5] = sx

  m[6] = cz * sy + sz * sx * cy
  m[7] = sz * sy - cz * sx * cy
  m[8] = cx * cy

  return m
}

/**
 * Ein BRUCHTEIL einer Drehung — dieselbe Achse, der `t`-fache Winkel.
 *
 * Der Weg führt über Achse und Winkel und nicht über die Euler-Winkel: die
 * addieren sich nicht, und ein Drittel dreier Euler-Winkel ist nicht ein Drittel
 * der Drehung. Für `t` zwischen 0 und 1 ist das Ergebnis dasselbe wie eine
 * kugelförmige Interpolation von der Ruhelage aus.
 *
 * Gebraucht für die Blickstabilisierung: der Kopf folgt der Rumpfdrehung nur zu
 * einem Anteil, und dieser Anteil muss eine Drehung sein und keine drei Zahlen.
 */
export function fractionOf(m: Mat3, t: number): Mat3 {
  const trace = m[0] + m[4] + m[8]
  const cosAngle = Math.min(1, Math.max(-1, (trace - 1) / 2))
  const angle = Math.acos(cosAngle)
  if (angle < 1e-9) return identity()

  const s = 2 * Math.sin(angle)
  const ax = (m[5] - m[7]) / s
  const ay = (m[6] - m[2]) / s
  const az = (m[1] - m[3]) / s

  /* Rodrigues: R = I + sin(θ)·K + (1 − cos(θ))·K², K die Kreuzproduktmatrix. */
  const a = angle * t
  const sa = Math.sin(a)
  const ca = 1 - Math.cos(a)

  const out = new Float64Array(9)
  out[0] = 1 + ca * (ax * ax - 1)
  out[1] = az * sa + ca * ax * ay
  out[2] = -ay * sa + ca * ax * az
  out[3] = -az * sa + ca * ax * ay
  out[4] = 1 + ca * (ay * ay - 1)
  out[5] = ax * sa + ca * ay * az
  out[6] = ay * sa + ca * ax * az
  out[7] = -ax * sa + ca * ay * az
  out[8] = 1 + ca * (az * az - 1)
  return out
}

/**
 * Winkelgeschwindigkeit aus zwei Orientierungen und dem Zeitschritt.
 *
 * Über die Drehung von `a` nach `b` als Achse-Winkel-Paar. Der Weg über die
 * Differenz der Euler-Winkel wäre falsch: Euler-Winkel addieren sich nicht, und
 * in der Nähe der Ausartung liefert die Differenz Unsinn.
 */
export function angularVelocity(a: Mat3, b: Mat3, dt: number): Vec3 {
  const rel = multiply(b, transpose(a))
  const trace = rel[0] + rel[4] + rel[8]
  const cosAngle = Math.min(1, Math.max(-1, (trace - 1) / 2))
  const angle = Math.acos(cosAngle)
  if (angle < 1e-9) return vec(0, 0, 0)

  const s = 2 * Math.sin(angle)
  const axis = {
    x: (rel[5] - rel[7]) / s,
    y: (rel[6] - rel[2]) / s,
    z: (rel[1] - rel[3]) / s,
  }
  return scale(axis, angle / dt)
}
